import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import axios from "axios";
import { useContext, useState } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { z } from "zod";
import {
  DeleteButton,
  SecondaryButtonWithPlusIcon,
  PrimaryButtonFitContainer,
} from "../../../../../components/Buttons/Buttons";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { SectionTitle } from "../../../../../components/Form/Form";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { endpoints } from "../../../../../endpoints";
import { Form } from "../../../../../layout/FormLayout";
import { SelectWithDeleteButton } from "../../../../../layout/shared";
import type { OptionType, UUID } from "../../../../../types/types";
import type {
  CollectionPaginatedOutputSchema,
  CollectionSchema,
} from "../../../../../types/types.PIM";
import { findNonEmptyDuplicateIndexes } from "../../../../../util/form.utils";
import {
  useStoreState,
  useFormWrapper,
  TEMPORARY_HIGH_LIMIT,
} from "../../../../../util/util";
import {
  zodSelectBoxDefault,
  zodSelectBoxType,
} from "../../../../../util/zod.util";
import {
  MarginBottomHeaderLeft,
  MarginBottomH6,
} from "../SellerAdminPIMAttributes/CreateAttribute";

const AddCollectionToTemplateSchema = z.object({
  first_collection: zodSelectBoxType,
  collections: z
    .object({
      collection: zodSelectBoxType,
    })
    .array()
    .optional(),
});

const AddCollectionToTemplateSchemaFn = (t: (s: string) => string) =>
  z
    .object({
      first_collection: zodSelectBoxDefault(t),
      collections: z
        .object({
          collection: zodSelectBoxDefault(t),
        })
        .array()
        .optional(),
    })
    .superRefine(({ first_collection, collections }, ctx) => {
      const duplicateIdxs = collections
        ? findNonEmptyDuplicateIndexes([
            first_collection.value,
            ...collections.map(({ collection: { value } }) => value),
          ])
        : [];
      if (duplicateIdxs.length > 0) {
        duplicateIdxs.forEach((idx) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Duplicate collections not allowed"),
            path: [`collections[${idx - 1}].collection`],
          });
        });
      }
    });

/**
 * At least one attribute is required, and we can't delete the first one. For
 * that reason the first attribute is not part of the `group` array.
 */
type formOutput = z.infer<typeof AddCollectionToTemplateSchema>;

export const collectionToOption = (
  collection: CollectionSchema
): OptionType<UUID> => ({
  value: collection.id,
  label: collection.name,
});

export function AddCollectionToTemplateForm({
  onSuccess,
  templateID,
  isTemplateInUse = false,
}: {
  onSuccess: () => void;
  templateID: UUID;
  isTemplateInUse?: boolean;
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [formValue, setFormValue] = useState<formOutput>();
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const methodsOfUseForm = useFormWrapper({
    resolver: zodResolver(AddCollectionToTemplateSchemaFn(t)),
    defaultValues: {
      first_collection: { label: undefined, value: undefined },
    },
  });
  const { handleSubmit, control, formState, errors } = methodsOfUseForm;
  const { notifyError, notifySuccess } = useContext(Notifications);

  const { fields, remove, append } = useFieldArray({
    control,
    name: "collections",
  });

  const { data: collections, error: collectionsError } = useSWR<
    CollectionPaginatedOutputSchema,
    AxiosError
  >(
    `${endpoints.v2_tenants_id_pim_collections(
      tenant_id
    )}?limit=${TEMPORARY_HIGH_LIMIT}`
  );

  const createFields = (groupOptions: OptionType<string>[]): JSX.Element[] => {
    return fields.map((field, index: number) => (
      <SelectWithDeleteButton key={field.id}>
        <Controller
          key={field.id}
          as={SelectBoxV2}
          control={control}
          isSearchBar
          name={`collections[${index}].collection`}
          defaultValue={{ label: "", value: "" }}
          placeholder={t("Collection Name")}
          options={groupOptions}
          rules={{ required: true }}
          errors={{
            [`collections[${index}].collection`]:
              errors?.collections?.[index]?.collection?.value ??
              errors?.collections?.[index]?.collection ??
              undefined,
          }}
          formState={formState}
        />
        <DeleteButton
          testid={`delete-button-${index}`}
          onClick={() => remove(index)}
          type="button"
          height={20}
          width={20}
        />
      </SelectWithDeleteButton>
    ));
  };

  const processForm = async (values: formOutput) => {
    let dataToPatch = {
      collections: [
        { collection_id: values.first_collection.value },
        ...(values.collections && values?.collections?.length > 0
          ? values?.collections?.map((collection) => {
              return { collection_id: collection.collection.value };
            })
          : []),
      ],
    };

    try {
      await axios.patch(
        `/v2/tenants/${tenant_id}/pim/templates/${templateID}`,
        dataToPatch
      );
      notifySuccess(t("Collection added successfully"));
      onSuccess();
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error adding the collection"),
        {
          error,
        }
      );
    }
  };

  const onSubmit = async (values: formOutput) => {
    if (isTemplateInUse) {
      setShowDialog(true);
      setFormValue(values);
    } else {
      await processForm(values);
    }
  };

  const isLoadingCollections = !collections && !collectionsError;

  const collectionOptions = collections?.data
    ?.map(collectionToOption)
    .sort((a, b) => a.label.localeCompare(b.label));

  if (isLoadingCollections) {
    return <DelayedSpinner />;
  }

  if (collectionsError) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the collections")}
      />
    );
  }

  return (
    <>
      <MarginBottomHeaderLeft>
        <SectionTitle>{t("Add Collection")}</SectionTitle>
      </MarginBottomHeaderLeft>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        {collectionOptions && (
          <>
            <MarginBottomH6>
              {t("Search from existing collections")}
            </MarginBottomH6>
            <div style={{ width: "420px" }}>
              <Controller
                as={SelectBoxV2}
                control={control}
                name="first_collection"
                placeholder={t("Collection name")}
                isSearchBar
                rules={{ required: true }}
                options={collectionOptions}
                errors={errors}
                formState={formState}
              />
            </div>

            {createFields(collectionOptions)}
            <SecondaryButtonWithPlusIcon
              type="button"
              style={{ marginBottom: "40px" }}
              onClick={() => append({ name: "" })}
            >
              {t("Add")}
            </SecondaryButtonWithPlusIcon>
          </>
        )}
        <PrimaryButtonFitContainer type="submit">
          {t("Save")}
        </PrimaryButtonFitContainer>
      </Form>
      <ConfirmDialog
        show={showDialog}
        closeDialog={() => {
          setFormValue(undefined);
          setShowDialog(false);
        }}
        confirmMessage={t(
          "Any changes will be pushed down to all products using this template. Do you want to continue?"
        )}
        handleConfirm={async () => {
          await processForm(formValue!);
          setShowDialog(false);
        }}
      />
    </>
  );
}
